import {
  Box,
  Button as MuiButton,
  ButtonProps,
  CircularProgress,
  CircularProgressProps,
  IconButton,
  SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import React, {ChangeEvent, useMemo, useState} from 'react';
import AccessControl from '../../../../security/components/access-control';
import {RolePageNames} from '../../../../security/model/role-page-names';
import {
  Button,
  LoadingOverlay,
  Status,
} from '@finpay-development/shared-components';
import AddIcon from '@mui/icons-material/Add';
import DocumentsModal from './documents-modal';
import {DocumentDeleteModal} from './document-delete-modal';
import _ from 'lodash';
import {SignatureStatus} from '../../models/signature-tracking';
import {
  StatusColors,
} from '@finpay-development/shared-components/lib/esm/utils/enums';
import {authDocDispatchMethod, authDocType} from '../../../../shared/enums';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import DeleteIcon from '@mui/icons-material/Delete';
import {PatientDocument} from './documents-accordion-contents-v2';
import {format} from 'date-fns';
import {FormikValues} from 'formik';

const buttonProps: ButtonProps = {
  variant: 'outlined',
  color: 'inherit',
  sx: {
    height: '3rem',
    borderWidth: '2px',
    borderColor: '#828282',
    minWidth: '5rem',
    boxShadow: 3,
    margin: '0.5rem 1rem 0 0',
  },
};

const iconProps: SvgIconProps = {
  fontSize: 'small',
  color: 'inherit',
};

const circularProgressProps: CircularProgressProps = {
  size: 24,
  style: {
    left: '1.3rem',
    position: 'absolute',
    marginTop: '0.5rem',
  },
};

function formatDocumentDate(value?: string | null): string {
  if (_.isNil(value) || value === '') {
    return '';
  }
  return format(value, 'MM/dd/yyyy');
}

const cellSx = {
  date: {
    width: '12%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  docName: {
    width: '25%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  status: {
    width: '16%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  uploadedBy: {
    width: '15%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  resend: {
    width: '20%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  delete: {
    width: '5%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },

};

type Props = {
  handleDownloadS3DocumentOnClick: (
      patientId: number, encounterId: number, s3DocumentName: string) => void
  handleDownloadHelloSignDocumentOnClick: (
      patientId: number, encounterId: number,
      externalDocumentId: string) => void
  isPatientDocumentsLoading: boolean
  documents: PatientDocument[]
  handleButtonOnClick: (
      docType: authDocType,
      paymentId: number | null,
      dispatchMethod: authDocDispatchMethod) => Promise<void>
  handleDocumentUploadOnClick: (formValues: FormikValues) => void
  handleDocumentDeleteOnClick: (selectedDocumentId: number) => () =>  void
  patientId: number
  patientEncounterId: number
}

export const DocumentsAccordionContentsV2View = ({
                                                   handleDownloadS3DocumentOnClick,
                                                   handleDownloadHelloSignDocumentOnClick,
                                                   isPatientDocumentsLoading,
                                                   documents,
                                                   handleButtonOnClick,
                                                   patientId,
                                                   patientEncounterId,
                                                   handleDocumentUploadOnClick,
                                                   handleDocumentDeleteOnClick
                                                 }: Props) => {

  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [isDocumentModelOpen, setDocumentModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectDocumentId, setSelectedDocumentId] = useState<number>(0);

  const getDocumentLink = (document: PatientDocument) => {

    const storageProvider = document.storageProvider;
    const documentName = document.authorizationDocumentStatus?.authorizationDocuments?.typeOfDoc ?
        getDocumentName(
            document.authorizationDocumentStatus?.authorizationDocuments?.typeOfDoc) :
        storageProvider === 's3' ?
            document.storageMetadata?.s3?.key.split('/').
                pop() :
            'N/A';

    if (storageProvider === 'dropbox' &&
        document.storageMetadata?.dropbox?.signatureRequestId ||
        storageProvider === 's3' && document.storageMetadata?.s3?.key
    ) {
      return (
          <a
              href="#"
              onClick={async (e) => {
                e.preventDefault();
                if (document.storageMetadata?.s3?.key) {

                  handleDownloadS3DocumentOnClick(
                      patientId,
                      patientEncounterId,
                      document.storageMetadata.s3?.key.split('/').pop(),
                  );

                } else if (document.storageMetadata?.dropbox?.signatureRequestId) {

                  handleDownloadHelloSignDocumentOnClick(
                      patientId,
                      patientEncounterId,
                      document.storageMetadata?.dropbox?.signatureRequestId,
                  );

                }

              }}
          >
            {documentName}
          </a>
      );
    } else {
      return documentName;
    }

  };

  const tableBody = useMemo(() => {
    return (
        <TableBody>
          {
            isPatientDocumentsLoading ? (
                <TableRow>
                  <TableCell
                      colSpan={7}
                      sx={{
                        position: 'relative',
                        border: 0,
                        minHeight: 240,
                        verticalAlign: 'middle',
                        textAlign: 'center',
                      }}
                  >
                    <LoadingOverlay whiteBackground={true}/>
                  </TableCell>
                </TableRow>
            ) : (

                documents.slice(
                    page * rowsPerPage,
                    page * rowsPerPage +
                    rowsPerPage,
                ).map((document) => {

                  const isAuthDoc = document.authorizationDocumentStatus;

                  return (
                      <TableRow
                          key={document.patientDocumentId}
                          test-id={`documents-accordion-v2-table-row-${document.patientDocumentId}`}
                      >
                        <TableCell
                            align="center"
                            sx={{py: 3, ...cellSx.date}}
                        >
                          <Typography
                              variant="h4"
                              test-id={`documents-accordion-v2-table-date-sent-${document.patientDocumentId}`}
                          >
                            {formatDocumentDate(
                                document.authorizationDocumentStatus?.dateSent,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{py: 3, ...cellSx.date}}
                        >
                          <Typography
                              variant="h4"
                              test-id={`documents-accordion-v2-table-date-approved-${document.patientDocumentId}`}
                          >
                            {formatDocumentDate(
                                document.authorizationDocumentStatus?.dateApproved,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{py: 3, ...cellSx.docName}}
                        >
                          <Typography
                              variant="h4"
                              test-id={`documents-accordion-v2-table-document-name-${document.patientDocumentId}`}
                          >
                            {getDocumentLink(document)}
                          </Typography>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                              verticalAlign: 'middle',
                              py: 3,
                              ...cellSx.status,
                            }}
                        >
                          {isAuthDoc ? (
                              <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    '& .status': {
                                      justifyContent: 'center',
                                    },
                                  }}
                                  test-id={`documents-accordion-v2-table-status-${document.patientDocumentId}`}
                              >
                                <Status
                                    text={_.capitalize(
                                        document.authorizationDocumentStatus!.documentStatus)}
                                    statusColor={(() => {
                                      switch (document.authorizationDocumentStatus?.documentStatus) {
                                        case SignatureStatus.signed :
                                          return StatusColors.success;
                                        case SignatureStatus.sent :
                                        case SignatureStatus.created :
                                          return StatusColors.warning;
                                        default :
                                          return StatusColors.error;
                                      }
                                    })()
                                    }
                                />
                              </Box>
                          ) : null}
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                              py: 3,
                              ...cellSx.uploadedBy,
                            }}
                        >
                          <Typography
                              variant="h4"
                              test-id={`documents-accordion-v2-table-upload-name-${document.patientDocumentId}`}
                          >
                            {
                              //Setting N/A for documents sent out automatically.
                              isAuthDoc ?
                                  'N/A' :
                                  `${_.capitalize(
                                      document.user.firstName)} ${_.capitalize(
                                      document.user.lastName)}`

                            }
                          </Typography>
                        </TableCell>
                        <TableCell align="center"
                                   sx={{py: 3, ...cellSx.resend}}>
                          {document.authorizationDocumentStatus?.authorizationDocuments?.isSigReq ===
                          false &&
                          document.authorizationDocumentStatus?.documentStatus !==
                          'signed' ? (
                              <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                              >
                                <MuiButton
                                    test-id={`documents-accordion-v2-table-send-email-${document.patientDocumentId}`}
                                    {...buttonProps}
                                    disabled={isSendingEmail}
                                    onClick={async () => {
                                      try {
                                        setIsSendingEmail(true);
                                        await handleButtonOnClick(
                                            document.authorizationDocumentStatus!.authorizationDocuments.typeOfDoc,
                                            document.authorizationDocumentStatus!.paymentId,
                                            authDocDispatchMethod.email,
                                        );
                                      } finally {
                                        setIsSendingEmail(false);
                                      }
                                    }}
                                >
                                  <EmailOutlinedIcon {...iconProps} />
                                  {isSendingEmail && (
                                      <CircularProgress {...circularProgressProps} />
                                  )}
                                </MuiButton>
                                {
                                    document.isEligibleForSms && (
                                        <MuiButton
                                            test-id={`documents-accordion-v2-table-send-sms-${document.patientDocumentId}`}
                                            {...buttonProps}
                                            disabled={isSendingSms}
                                            onClick={async () => {
                                              try {
                                                setIsSendingSms(true);
                                                await handleButtonOnClick(
                                                    document.authorizationDocumentStatus!.authorizationDocuments.typeOfDoc,
                                                    document.authorizationDocumentStatus!.paymentId,
                                                    authDocDispatchMethod.sms,
                                                );
                                              } finally {
                                                setIsSendingSms(false);
                                              }
                                            }}
                                        >
                                          <ChatBubbleIcon {...iconProps} />
                                          {isSendingSms && (
                                              <CircularProgress {...circularProgressProps} />
                                          )}
                                        </MuiButton>
                                    )
                                }
                              </Box>
                          ) : null}
                        </TableCell>
                        <TableCell align="center"
                                   sx={{py: 3, ...cellSx.delete}}>
                          {!isAuthDoc && (
                              <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                              >
                                <IconButton
                                    test-id={`documents-accordion-v2-table-delete-${document.patientDocumentId}`}
                                    aria-haspopup="true"
                                    onClick={() => {
                                      setSelectedDocumentId(
                                          document.patientDocumentId);
                                      setIsOpen(true);
                                    }}
                                    size="large">
                                  <DeleteIcon/>
                                </IconButton>
                              </Box>
                          )}
                        </TableCell>
                      </TableRow>
                  );
                })
            )
          }
        </TableBody>
    );

  }, [
    documents,
    page,
    rowsPerPage,
    isSendingEmail,
    isSendingSms,
    isPatientDocumentsLoading]);

  return (
      <div
          className="list-header documents-accordion px-4 pb-4 pt-2"
          test-id="document-status-accordion-v2"
      >
        <TableContainer
            sx={{mt: 1, mb: 1}}
        >
          <Table
              size="small"
              sx={{tableLayout: 'fixed', width: '100%'}}
          >
            <TableHead>
              <TableRow className="header px-1 mb-1">
                <TableCell align="center" sx={{py: 2, ...cellSx.date}}>
                  <Typography
                      variant="h4">
                    Date Sent
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.date}}>
                  <Typography variant="h4">
                    Date Signed
                  </Typography>
                </TableCell>
                <TableCell
                    align="center"
                    sx={{py: 2, ...cellSx.docName}}
                >
                  <Typography
                      variant="h4"
                  >
                    Document Name
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.status}}>
                  <Typography
                      variant="h4"
                  >
                    Status
                  </Typography>
                </TableCell>
                <TableCell
                    align="center"
                    sx={{py: 2, ...cellSx.uploadedBy}}
                >
                  <Typography variant="h4">Uploaded By</Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.resend}}>
                  <Typography variant="h4">Re-Send</Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.delete}}>
                </TableCell>
              </TableRow>
            </TableHead>
            {tableBody}
          </Table>
          <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
              }}
          >
            <TablePagination
                SelectProps={{
                  inputProps: {
                    'test-id': 'documents-accordion-v2-rows-per-page-select',
                  },
                }}
                component="div"
                rowsPerPageOptions={[
                  5,
                  10,
                  15,
                ]}
                count={Array.isArray(documents) ? documents.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage={'View'}
                onPageChange={(
                    event: unknown, newPage: number) => {
                  setPage(newPage);
                }}
                onRowsPerPageChange={(
                    event: ChangeEvent<HTMLInputElement>,
                ) => {
                  setRowsPerPage(+event.target.value);
                  setPage(0);
                }}
            />
          </Box>
        </TableContainer>
        <AccessControl
            rolePageName={RolePageNames.PatientRecords}
            actionName="Add Instance of Care"
            renderNoAccess={() => <Button disabled>Add Document</Button>}
        >
          <Button
              type="text"
              icon={<AddIcon/>}
              paddingLeft={0}
              marginTop={4}
              onClick={() => {
                setDocumentModalOpen(true);
              }}
          >
            Add Document
          </Button>
        </AccessControl>

        <DocumentsModal
            patientID={patientId}
            encounterId={patientEncounterId}
            open={isDocumentModelOpen}
            onClose={() => {
              setDocumentModalOpen(false);
            }}
            onDocumentUpload={handleDocumentUploadOnClick}
        />
        {
            isOpen && (
                <DocumentDeleteModal
                    selectedDocumentId={selectDocumentId}
                    setIsOpen={setIsOpen}
                    deleteOnClick={async () => {
                      handleDocumentDeleteOnClick(selectDocumentId)()
                      setIsOpen(false);
                    }}
                />
            )
        }

      </div>
  );

};

const getDocumentName = (typeOfDoc?: string) => {
  switch (typeOfDoc) {
    case 'inst' :
      return 'Installment Agreement';
    case 'spa' :
      return 'Single Pay Agreement';
    case 'pfr' :
      return 'PFR Adjustment/Refinance Agreement';
    case 'phi' :
      return 'PHI Consent';
    case 'otp' :
      return 'One Time Payment Agreement';
    default :
      return '';
  }
};